import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle, ShieldCheck, Wrench, Sprout } from "lucide-react";
import { Hero } from "../components/Hero";
import { Marquee } from "../components/Marquee";
import { Reveal } from "../components/Reveal";
import { ProductShot } from "../components/ProductCard";
import { PRODUCTS, waLink, WHATSAPP_DISPLAY } from "../lib/site";

const QuoteBar = () => (
  <section className="bg-mist" data-testid="quote-bar">
    <div className="container-x flex flex-col items-start justify-between gap-5 py-7 md:flex-row md:items-center">
      <div>
        <p className="font-display text-2xl font-bold uppercase leading-none text-ink sm:text-3xl">
          Precisa de um implemento? <span className="text-brand">Fale com um especialista.</span>
        </p>
        <p className="mt-2 text-sm text-steel">Atendimento comercial pelo WhatsApp {WHATSAPP_DISPLAY}</p>
      </div>
      <a href={waLink()} target="_blank" rel="noreferrer" className="btn-red" data-testid="quote-bar-whatsapp-btn">
        <MessageCircle className="h-4 w-4" /> Solicitar orçamento agora
      </a>
    </div>
  </section>
);

const Featured = () => (
  <section className="container-x py-14 lg:py-16" data-testid="featured-products">
    <div className="mb-8 flex items-end justify-between gap-4">
      <Reveal>
        <p className="eyebrow">Produtos em destaque</p>
        <h2 className="mt-2 font-display text-4xl font-bold uppercase leading-none text-ink sm:text-5xl">Feitos para a lida pesada</h2>
      </Reveal>
      <Link to="/produtos" className="link-arrow hidden sm:inline-flex" data-testid="featured-see-all">Ver todos <ArrowRight className="h-4 w-4" /></Link>
    </div>
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {PRODUCTS.map((p, i) => (
        <Reveal key={p.slug} delay={(i % 4) * 0.06}>
          <Link to={`/produtos#${p.slug}`} data-testid={`featured-${p.slug}`}
            className="group block overflow-hidden rounded-xl border border-[#344D5B] bg-white transition-shadow duration-500 hover:shadow-[0_20px_45px_-25px_rgba(17,24,39,0.4)]">
            <ProductShot p={p} className="aspect-[4/3]" field scale={1} />
            <p className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wide text-ink sm:text-sm">{p.name}</p>
          </Link>
        </Reveal>
      ))}
      <Reveal delay={0.18}>
        <Link to="/produtos" data-testid="featured-pecas"
          className="group block overflow-hidden rounded-xl border border-[#344D5B] bg-white transition-shadow duration-500 hover:shadow-[0_20px_45px_-25px_rgba(17,24,39,0.4)]">
          <div className="aspect-[4/3] overflow-hidden">
            <img src="/img/pecas.jpg" alt="Peças de reposição SOLLUS" loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          <p className="px-3 py-3 text-center text-xs font-bold uppercase tracking-wide text-ink sm:text-sm">Peças</p>
        </Link>
      </Reveal>
    </div>
    <Link to="/produtos" className="link-arrow mt-6 inline-flex sm:hidden" data-testid="featured-see-all-mobile">Ver todos os produtos <ArrowRight className="h-4 w-4" /></Link>
  </section>
);

const PILLARS = [
  { icon: ShieldCheck, t: "Qualidade", d: "Segurança e robustez em cada equipamento." },
  { icon: Wrench, t: "Assistência técnica", d: "Reparo e revisão com equipe qualificada." },
  { icon: Sprout, t: "Tecnologia", d: "Tradição aliada à inovação no campo." },
];

const About = () => (
  <section className="border-t border-line bg-white" data-testid="home-about">
    <div className="container-x grid items-center gap-10 py-14 lg:grid-cols-2 lg:py-16">
      <Reveal className="relative">
        <div className="clip-notch overflow-hidden rounded-lg">
          <img src="/img/interna.jpg" alt="Linha de produção SOLLUS em Assis/SP" loading="lazy" className="aspect-[16/10] w-full object-cover" />
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="eyebrow">Sobre a SOLLUS</p>
        <h2 className="mt-2 font-display text-4xl font-bold uppercase leading-none text-ink sm:text-5xl">Desde 1996 no campo brasileiro</h2>
        <p className="mt-4 text-base text-steel">
          Fundada em Assis/SP, a SOLLUS fabrica máquinas e implementos para cana-de-açúcar, grãos e a linha completa de apoio, com presença em todo o Brasil e exportações para Estados Unidos, Paraguai, Chile, Angola e Guiana Francesa.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {PILLARS.map(({ icon: I, t, d }) => (
            <div key={t} className="border-l-2 border-brand pl-3">
              <I className="h-5 w-5 text-brand" />
              <p className="mt-2 text-sm font-bold uppercase tracking-wide text-ink">{t}</p>
              <p className="mt-1 text-xs leading-relaxed text-steel">{d}</p>
            </div>
          ))}
        </div>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link to="/sobre" className="btn-dark" data-testid="home-about-btn">Conheça a SOLLUS <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/produtos" className="btn-ghost" data-testid="home-products-btn">Ver produtos</Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <QuoteBar />
      <Featured />
      <Marquee />
      <About />
    </div>
  );
}
